import { FC, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import __ from "src/utils/Translations";
import { NotificationType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import TooltipInfo from "src/Pages/Company/Absences/Forms/TooltipInfo";

interface CheckboxOption {
  name: string;
  label: string;
  helpText?: string;
}

interface NestedNotificationCheckboxProps {
  parentLabel: string;
  parentName: string;
  options: CheckboxOption[];
  basePath: string;
  disabled: boolean;
}

const NestedNotificationCheckbox: FC<NestedNotificationCheckboxProps> = ({
  parentLabel,
  parentName,
  options,
  basePath,
  disabled,
}) => {
  const { control, setValue } = useFormContext();

  const watchedValues = useWatch({
    control,
    name: options.map((option) => `${basePath}.${parentName}.${option.name}`),
  });

  const areAllChecked = useMemo(
    () => () =>
      options.every((_, index) => {
        const value = watchedValues[index];
        return value.includes(NotificationType.EMAIL);
      }),
    [watchedValues, options]
  );

  const areSomeChecked = useMemo(
    () => () => {
      const checkedCount = options.filter((_, index) => {
        const value = watchedValues[index];
        return value.includes(NotificationType.EMAIL);
      }).length;

      return checkedCount > 0 && checkedCount < options.length;
    },
    [watchedValues, options]
  );

  const handleParentChange = (checked: boolean) => {
    options.forEach((option) => {
      setValue(
        `${basePath}.${parentName}.${option.name}`,
        checked ? [NotificationType.EMAIL] : []
      );
    });
  };

  return (
    <>
      <FormControlLabel
        label={__(parentLabel)}
        disabled={disabled}
        slotProps={{
          typography: { sx: { fontWeight: 700 } },
        }}
        control={
          <Checkbox
            checked={areAllChecked()}
            indeterminate={areSomeChecked()}
            onChange={(_, checked) => handleParentChange(checked)}
          />
        }
      />

      <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
        {options.map((option) => (
          <Controller
            key={option.name}
            name={`${basePath}.${parentName}.${option.name}`}
            control={control}
            render={({ field }) => (
              <Stack direction="row" sx={{ alignItems: "baseline" }}>
                <FormControlLabel
                  label={__(option.label)}
                  disabled={disabled}
                  control={
                    <Checkbox
                      checked={(field.value || []).includes(
                        NotificationType.EMAIL
                      )}
                      onChange={(_, checked) => {
                        field.onChange(checked ? [NotificationType.EMAIL] : []);
                      }}
                    />
                  }
                  sx={{ mr: 0 }}
                />
                {option.helpText && (
                  <TooltipInfo title={<>{option.helpText}</>} />
                )}
              </Stack>
            )}
          />
        ))}
      </Box>
    </>
  );
};

export default NestedNotificationCheckbox;
